/*
 * sticky-variant-dropdown.js
 *
 * Custom variant dropdown for the sticky ATC bar. Mirrors the PDP variant
 * picker dropdown's look, but opens UPWARD (the sticky bar sits at the bottom
 * of the viewport). Progressive enhancement over the sibling hidden
 * <select name="id">: the select stays the form value + source of truth, and
 * this dropdown drives it (option click -> set select value + dispatch change,
 * which sticky-atc-bar.js already listens to for the variant sync).
 *
 * The reverse direction (main product variant change -> sticky select value
 * updated by sticky-atc-bar.js WITHOUT a change event) is handled by listening
 * for the 'sticky:refresh' event sticky-atc-bar.js dispatches on the select, so
 * the label/selected state stay in sync without a change-event loop.
 */
if (!customElements.get('sticky-variant-dropdown')) {
  customElements.define(
    'sticky-variant-dropdown',
    class StickyVariantDropdown extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-sticky-dropdown-toggle]');
        this.label = this.querySelector('[data-sticky-dropdown-label]');
        this.options = Array.from(this.querySelectorAll('[data-sticky-dropdown-option]'));
        const field = this.closest('.sticky-atc-bar__variant-field');
        this.select = field ? field.querySelector('select[name="id"]') : null;
        if (!this.toggle || !this.select || !this.options.length) return;

        this.onDocClick = (e) => {
          if (!this.contains(e.target)) this.setOpen(false);
        };
        this.onKeydown = (e) => {
          if (e.key === 'Escape') this.setOpen(false);
        };

        this.toggle.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.setOpen(this.getAttribute('data-state') !== 'open');
        });

        this.options.forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.preventDefault();
            this.selectValue(opt.getAttribute('data-value'), true);
            this.setOpen(false);
          });
        });

        // Keep the UI in sync when the value changes elsewhere.
        this.select.addEventListener('change', () => this.syncFromSelect());
        this.select.addEventListener('sticky:refresh', () => this.syncFromSelect());

        document.addEventListener('click', this.onDocClick, true);
        document.addEventListener('keydown', this.onKeydown);

        this.setupDragScroll();
        this.setBoxWidth();
        this.onResize = () => this.setBoxWidth();
        window.addEventListener('resize', this.onResize);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.setBoxWidth());
        }
        this.syncFromSelect();
      }

      // Size the box to the LONGEST variant row (measured off-flow), so no option truncates.
      setBoxWidth() {
        const sizer = this.querySelector('[data-sticky-dropdown-sizer]');
        if (!sizer) return;
        const w = Math.ceil(sizer.getBoundingClientRect().width);
        if (w > 0) this.style.width = w + 'px';
      }

      // Grab-and-drag to scroll the (scrollbar-hidden) option list. A drag past a small
      // threshold suppresses the option click so dragging never selects a variant.
      setupDragScroll() {
        const menu = this.querySelector('[data-sticky-dropdown-menu]');
        if (!menu) return;
        let down = false;
        let startY = 0;
        let startScroll = 0;
        let moved = 0;
        menu.addEventListener('pointerdown', (e) => {
          if (e.button != null && e.button !== 0) return;
          down = true;
          moved = 0;
          startY = e.clientY;
          startScroll = menu.scrollTop;
          menu.classList.add('is-dragging');
        });
        menu.addEventListener('pointermove', (e) => {
          if (!down) return;
          const dy = e.clientY - startY;
          if (Math.abs(dy) > moved) moved = Math.abs(dy);
          menu.scrollTop = startScroll - dy;
        });
        const end = () => {
          if (!down) return;
          down = false;
          menu.classList.remove('is-dragging');
        };
        menu.addEventListener('pointerup', end);
        menu.addEventListener('pointerleave', end);
        menu.addEventListener('pointercancel', end);
        menu.addEventListener(
          'click',
          (e) => {
            if (moved > 6) {
              e.preventDefault();
              e.stopPropagation();
            }
            moved = 0;
          },
          true
        );
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick, true);
        document.removeEventListener('keydown', this.onKeydown);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      setOpen(open) {
        this.setAttribute('data-state', open ? 'open' : 'closed');
        if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          const menu = this.querySelector('[data-sticky-dropdown-menu]');
          if (menu) {
            if (!menu.__moreWired) {
              menu.__moreWired = true;
              menu.addEventListener('scroll', () => this.updateMore(menu), { passive: true });
            }
            requestAnimationFrame(() => this.updateMore(menu));
          }
        }
      }

      // "More below" scroll cue (edge-fade pattern — see
      // docs/theme_function/thumbnail-edge-fade-gradient.md): show the sticky fade + chevron
      // (last li, `data-visible`) only while the menu's content overflows its
      // max-height AND it isn't scrolled to the end.
      updateMore(menu) {
        const more = menu.querySelector('[data-sticky-dropdown-more]');
        if (!more) return;
        const overflow = menu.scrollHeight - menu.clientHeight;
        const atBottom = menu.scrollTop >= overflow - 2;
        more.setAttribute('data-visible', overflow > 2 && !atBottom ? 'true' : 'false');
      }

      selectValue(value, fire) {
        if (!value) return;
        this.select.value = value;
        if (fire) this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncFromSelect();
      }

      syncFromSelect() {
        const value = this.select.value;
        let selectedOpt = null;
        this.options.forEach((opt) => {
          const isSel = opt.getAttribute('data-value') === value;
          if (isSel) selectedOpt = opt;
          opt.setAttribute('data-selected', isSel ? 'true' : 'false');
          opt.style.backgroundColor = isSel ? 'var(--sticky-dd-selected, #A1E8E3)' : 'transparent';
          opt.setAttribute('aria-pressed', isSel ? 'true' : 'false');
        });
        if (!selectedOpt) return;
        if (this.label) {
          this.label.textContent = (selectedOpt.textContent || '').trim();
        }
        // Mirror the selected option's colour swatch onto the toggle.
        const toggleSwatch = this.querySelector('[data-sticky-dropdown-selected-swatch]');
        if (toggleSwatch) {
          const hex = selectedOpt.getAttribute('data-swatch-hex') || '';
          const img = selectedOpt.getAttribute('data-swatch-img') || '';
          if (hex) {
            toggleSwatch.style.backgroundColor = hex;
            toggleSwatch.style.backgroundImage = 'none';
            toggleSwatch.classList.remove('tw-hidden');
          } else if (img) {
            toggleSwatch.style.backgroundColor = 'transparent';
            toggleSwatch.style.backgroundImage = 'url(' + img + ')';
            toggleSwatch.style.backgroundSize = 'cover';
            toggleSwatch.style.backgroundPosition = 'center';
            toggleSwatch.classList.remove('tw-hidden');
          } else {
            toggleSwatch.classList.add('tw-hidden');
          }
        }
      }
    }
  );
}
